/**
 * SkyDent damage-record service.
 *
 * SkyDent is a separate application (same AWS account) that records aircraft
 * damage; its records live in a KMS-encrypted DynamoDB table. Aether reaches
 * them through its own backend proxy (never the browser directly), correlated
 * to an Aether event by event id.
 *
 * Used to (1) detect whether a correlating SkyDent record exists yet — so the
 * SkyDent affordance only appears once SkyDent has ingested the damage event —
 * and (2) read/write that record's editable fields from an in-Aether modal so
 * changes propagate to SkyDent-only users.
 */
import { authedFetch } from './apiClient';
import { createLogger } from '../utils/logger';

const logger = createLogger('SkyDentService');

/** A single SkyDent photo attachment. */
export interface SkyDentPhoto {
  id?: string;
  filename: string;
  contentType: string;
  key?: string;
  url?: string;
  uploaded_at?: string;
}

/** Presigned-upload handle returned by the photo-upload proxy route. */
export interface SkyDentUploadHandle {
  uploadUrl: string;
  key: string;
  photoId: string;
  filename: string;
}

/** Content types SkyDent accepts for photo attachments. */
export const SKYDENT_ALLOWED_PHOTO_TYPES = [
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
  'image/bmp',
  'image/heic',
  'application/pdf',
];

/** The four editable SkyDent user-data fields. */
export interface SkyDentEditableData {
  root_cause: string;
  notes: string;
  damage_location: string;
  photos: SkyDentPhoto[];
}

/** A SkyDent record plus its editable field values. */
export interface SkyDentRecordView {
  exists: boolean;
  record: Record<string, unknown> | null;
  editableFields: string[];
  editable: SkyDentEditableData;
}

/** Normalizes the raw editable payload into typed, defaulted values. */
function toEditable(raw: unknown): SkyDentEditableData {
  const source = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {};
  const photos = Array.isArray(source.photos) ? (source.photos as SkyDentPhoto[]) : [];
  return {
    root_cause: typeof source.root_cause === 'string' ? source.root_cause : '',
    notes: typeof source.notes === 'string' ? source.notes : '',
    damage_location: typeof source.damage_location === 'string' ? source.damage_location : '',
    photos,
  };
}

/**
 * Fetches the correlating SkyDent record for an event. Resolves with
 * `{ exists: false, ... }` when none exists, the binding is unconfigured, or the
 * request fails — so callers can simply hide the affordance without special-casing.
 */
export async function lookupSkyDentRecord(eventId: string): Promise<SkyDentRecordView> {
  const empty: SkyDentRecordView = {
    exists: false,
    record: null,
    editableFields: [],
    editable: toEditable(null),
  };
  if (!eventId) return empty;
  try {
    const response = await authedFetch(`/events/${encodeURIComponent(eventId)}/skydent`);
    if (!response || !response.ok) return empty;
    const data = (await response.json()) as {
      exists?: boolean;
      record?: Record<string, unknown> | null;
      editableFields?: string[];
      editable?: unknown;
    };
    return {
      exists: !!data.exists,
      record: data.record ?? null,
      editableFields: Array.isArray(data.editableFields) ? data.editableFields : [],
      editable: toEditable(data.editable),
    };
  } catch (err: unknown) {
    logger.error('lookupSkyDentRecord failed', {
      eventId,
      error: err instanceof Error ? err.message : 'Unknown error',
    });
    return empty;
  }
}

/** Result of a SkyDent save: ok, plus any server validation messages. */
export interface SkyDentSaveResult {
  ok: boolean;
  errors?: string[];
  message?: string;
}

/**
 * Writes the edited SkyDent fields back through the proxy (which forwards to
 * SkyDent's API under the service account). Only the four editable fields are
 * persisted; SkyDent re-validates and gates on its own edit group. Returns
 * `{ ok }` plus any validation messages the API returned.
 */
export async function saveSkyDentRecord(
  eventId: string,
  fields: SkyDentEditableData,
): Promise<SkyDentSaveResult> {
  try {
    const response = await authedFetch(`/events/${encodeURIComponent(eventId)}/skydent`, {
      method: 'PUT',
      includeContentType: true,
      body: JSON.stringify({ fields }),
    });
    if (!response) return { ok: false, message: 'Network error' };
    if (response.ok) return { ok: true };
    const data = (await response.json().catch(() => ({}))) as {
      message?: string;
      errors?: string[];
    };
    return { ok: false, message: data.message, errors: data.errors };
  } catch (err: unknown) {
    logger.error('saveSkyDentRecord failed', {
      eventId,
      error: err instanceof Error ? err.message : 'Unknown error',
    });
    return { ok: false, message: 'Request failed' };
  }
}

/**
 * Requests a presigned S3 upload for a SkyDent photo through the proxy route.
 * The proxy forwards to SkyDent's `POST /v1/api/photos/upload/{eventId}` under
 * the service account and returns the presigned URL plus the assigned key and
 * photo id. Resolves to null when the request could not be authenticated or the
 * server rejected it.
 */
export async function requestPhotoUpload(
  eventId: string,
  filename: string,
  contentType: string,
): Promise<SkyDentUploadHandle | null> {
  try {
    const response = await authedFetch(`/events/${encodeURIComponent(eventId)}/skydent/photos`, {
      method: 'POST',
      includeContentType: true,
      body: JSON.stringify({ filename, contentType }),
    });
    if (!response || !response.ok) return null;
    const data = (await response.json()) as Partial<SkyDentUploadHandle>;
    if (!data.uploadUrl || !data.key || !data.photoId) return null;
    return {
      uploadUrl: data.uploadUrl,
      key: data.key,
      photoId: data.photoId,
      filename: data.filename ?? filename,
    };
  } catch (err: unknown) {
    logger.error('requestPhotoUpload failed', {
      eventId,
      filename,
      error: err instanceof Error ? err.message : 'Unknown error',
    });
    return null;
  }
}

/**
 * Uploads the raw photo bytes directly to the presigned S3 URL. The presigned
 * URL carries its own authorization, so this is an unauthenticated PUT (no
 * bearer token). Resolves true on success.
 */
export async function uploadPhotoBytes(
  uploadUrl: string,
  file: File,
  contentType: string,
): Promise<boolean> {
  try {
    const response = await fetch(uploadUrl, {
      method: 'PUT',
      headers: { 'Content-Type': contentType },
      body: file,
    });
    return response.ok;
  } catch (err: unknown) {
    logger.error('uploadPhotoBytes failed', {
      error: err instanceof Error ? err.message : 'Unknown error',
    });
    return false;
  }
}
